import { useEffect } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const EASE = "power3.out";

function splitIntoWords(el) {
  if (el.dataset.splitDone === "1") {
    return Array.from(el.querySelectorAll(":scope > span.word"));
  }
  const words = el.textContent.split(/\s+/).filter(Boolean);
  el.textContent = "";
  const spans = words.map((word, i) => {
    const span = document.createElement("span");
    span.className = "word";
    span.style.display = "inline-block";
    span.style.willChange = "transform, opacity";
    span.textContent = word + (i < words.length - 1 ? " " : "");
    el.appendChild(span);
    return span;
  });
  el.dataset.splitDone = "1";
  return spans;
}

// Wires the five scroll-reveal primitives onto every matching descendant of
// `ref`, scoped with gsap.context() so cleanup on unmount (or when `deps`
// changes) is exhaustive and closures never go stale:
//   data-anim              fade up (opacity 0->1, y 40->0)
//   data-anim-delay="0.4"  optional per-element delay in seconds
//   data-mask               clip-path wipe, top to bottom
//   data-stagger             container — descendant [data-anim] fire as a
//                            group when the container enters view, staggered
//   data-draw                SVG <path> stroke draw-in
//   data-parallax="0.15"    scroll-linked vertical translate, value = speed
//   data-split               splits text into words, staggers each word in
// Side effects only — nothing is returned. Honors prefers-reduced-motion by
// skipping every GSAP timeline and snapping elements to their final state.
export function useReveal(ref, deps = []) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const root = ref?.current;
    if (!root) return;

    const prefersReduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    const ctx = gsap.context(() => {
      // data-split runs first and synchronously so the DOM is split into
      // word spans before anything else queries the subtree.
      root.querySelectorAll("[data-split]").forEach((el) => {
        const words = splitIntoWords(el);
        if (prefersReduced) {
          gsap.set(words, { opacity: 1, y: 0 });
          return;
        }
        gsap.set(words, { opacity: 0, y: 24 });
        gsap.to(words, {
          opacity: 1,
          y: 0,
          duration: 0.7,
          ease: EASE,
          stagger: 0.06,
          scrollTrigger: { trigger: el, start: "top 85%", once: true },
        });
      });

      // data-stagger containers own their descendant [data-anim] elements —
      // those fire together, staggered, when the container enters view.
      const staggered = new Set();
      root.querySelectorAll("[data-stagger]").forEach((container) => {
        const children = container.querySelectorAll(":scope [data-anim]");
        children.forEach((c) => staggered.add(c));
        if (prefersReduced) {
          gsap.set(children, { opacity: 1, y: 0 });
          return;
        }
        gsap.set(children, { opacity: 0, y: 40 });
        gsap.to(children, {
          opacity: 1,
          y: 0,
          duration: 0.8,
          ease: EASE,
          stagger: 0.08,
          scrollTrigger: { trigger: container, start: "top 80%", once: true },
        });
      });

      // Standalone [data-anim] elements (not owned by a [data-stagger]
      // ancestor) reveal individually as each one enters the viewport.
      root.querySelectorAll("[data-anim]").forEach((el) => {
        if (staggered.has(el)) return;
        const delay = Number(el.dataset.animDelay || 0);
        if (prefersReduced) {
          gsap.set(el, { opacity: 1, y: 0 });
          return;
        }
        gsap.set(el, { opacity: 0, y: 40 });
        gsap.to(el, {
          opacity: 1,
          y: 0,
          duration: 0.8,
          ease: EASE,
          delay,
          scrollTrigger: { trigger: el, start: "top 85%", once: true },
        });
      });

      root.querySelectorAll("[data-mask]").forEach((el) => {
        if (prefersReduced) {
          gsap.set(el, { clipPath: "inset(0% 0% 0% 0%)" });
          return;
        }
        gsap.set(el, { clipPath: "inset(100% 0% 0% 0%)" });
        gsap.to(el, {
          clipPath: "inset(0% 0% 0% 0%)",
          duration: 0.9,
          ease: EASE,
          scrollTrigger: { trigger: el, start: "top 85%", once: true },
        });
      });

      root.querySelectorAll("[data-draw]").forEach((path) => {
        if (typeof path.getTotalLength !== "function") return;
        const length = path.getTotalLength();
        if (prefersReduced) {
          gsap.set(path, { strokeDasharray: length, strokeDashoffset: 0 });
          return;
        }
        gsap.set(path, { strokeDasharray: length, strokeDashoffset: length });
        gsap.to(path, {
          strokeDashoffset: 0,
          duration: 1.4,
          ease: "power2.inOut",
          scrollTrigger: { trigger: path, start: "top 85%", once: true },
        });
      });

      // Parallax is a scroll-linked motion effect by definition — reduced
      // motion means it doesn't run at all, not that it snaps anywhere.
      // Movement is scaled to the element's own height, not viewport height —
      // a 0.15 speed on a 300px thumbnail should move ~45px, not ~130px, or
      // it overshoots small cards and shows gaps at the overflow-hidden edge.
      if (!prefersReduced) {
        root.querySelectorAll("[data-parallax]").forEach((el) => {
          const speed = parseFloat(el.dataset.parallax) || 0.15;
          const travel = el.getBoundingClientRect().height * speed;
          gsap.fromTo(
            el,
            { y: -travel / 2 },
            {
              y: travel / 2,
              ease: "none",
              scrollTrigger: {
                trigger: el,
                start: "top bottom",
                end: "bottom top",
                scrub: true,
              },
            },
          );
        });
      }

      ScrollTrigger.refresh();
    }, root);

    return () => ctx.revert();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref, ...deps]);
}
